import type { ReactNode } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { classNames } from "primereact/utils";

import CheckIcon from "@/assets/icons/equipe-check.svg?react";
import PencilIcon from "@/assets/icons/equipe-pencil.svg?react";
import TimesIcon from "@/assets/icons/equipe-times.svg?react";
import TrashIcon from "@/assets/icons/equipe-trash.svg?react";
import { Calendar, Dropdown, FormField, Icon } from "@/ds";
import {
  EQUIPE_COLUMN_FIM,
  EQUIPE_COLUMN_INICIO,
  EQUIPE_DATA_PLACEHOLDER,
  EQUIPE_DISPONIVEL,
  EQUIPE_INDISPONIVEL,
  EQUIPE_PAPEL_PLACEHOLDER,
  EQUIPE_PROFISSIONAL_EMPTY,
  EQUIPE_PROFISSIONAL_PLACEHOLDER,
} from "../equipe-obra.constants";
import { ConflitosTag } from "../equipe-conflitos-tag";
import type { PapelOpcao, ProfissionalOpcao } from "../equipe-obra.types";
import styles from "./alocacao-celulas.module.scss";
import type {
  NomeCelulaProps,
  PapelCelulaProps,
  DataCelulaProps,
  AcoesCelulaProps,
  NomeCelulaViewModel,
  SituacaoProfissional,
  PapelCelulaViewModel,
  DataCelulaViewModel,
  AcoesCelulaViewModel,
} from "./types-alocacao-celulas";

// Rótulo e gatilho dos selects da linha no padding do input pequeno do Figma (11px da borda externa).
const PT_SELECT = {
  input: { className: styles.rotulo },
  trigger: { className: styles.gatilho },
};
const PT_PROFISSIONAL = {
  ...PT_SELECT,
  input: { className: `${styles.rotulo} ${styles.rotuloNome}` },
  filterInput: { className: styles.filtro },
};

// Área rolável do menu de profissionais: 198,5px (Figma 9129:353328).
const ALTURA_LISTA_PROFISSIONAL = "198.5px";

/**
 * Situação do profissional (Figma 9129:353328: tag de 10px no mesmo lugar, `tag/success` ou `tag/warn`; o menu
 * não tem tag vermelha): "Indisponível" e "N Conflitos" aparecem juntas; "Disponível" só sem nenhuma das duas.
 */
function SituacaoTags({ situacao }: { situacao: SituacaoProfissional }) {
  return (
    <span className={styles.tags}>
      {situacao.indisponivel ? (
        <Tag
          severity="warning"
          className={classNames("p-tag-xs", styles.disponibilidade)}
          value={EQUIPE_INDISPONIVEL}
        />
      ) : null}
      {situacao.conflitos > 0 ? <ConflitosTag quantidade={situacao.conflitos} /> : null}
      {situacao.disponivel ? (
        <Tag
          severity="success"
          className={classNames("p-tag-xs", styles.disponibilidade)}
          value={EQUIPE_DISPONIVEL}
        />
      ) : null}
    </span>
  );
}

/** Nome: leitura (texto + disponibilidade) ou o select de profissional com busca. */
export function NomeCelulaView({
  indice,
  linha,
  opcoes,
  profissionaisError,
  disabled = false,
  situacaoDe,
  selecionado,
  nome,
  editavel,
  erro,
  erroLeitura,
  testId,
  escolherProfissional,
}: NomeCelulaProps & NomeCelulaViewModel) {
  if (!editavel) {
    return (
      <FormField.Root className={styles.campo} error={erroLeitura} testId={testId}>
        <div className={styles.leituraNome}>
          <span className={styles.truncavel} data-pr-tooltip={nome} data-pr-position="top">
            {nome}
          </span>
          {selecionado ? <SituacaoTags situacao={situacaoDe(selecionado)} /> : null}
        </div>
        <FormField.Error />
      </FormField.Root>
    );
  }

  return (
    <FormField.Root className={styles.campo} error={erro} testId={testId}>
      <FormField.Control>
        <Dropdown
          size="sm"
          className={styles.select}
          pt={PT_PROFISSIONAL}
          scrollHeight={ALTURA_LISTA_PROFISSIONAL}
          panelClassName={styles.painelProfissional}
          value={linha.usuarioId}
          options={opcoes}
          optionLabel="label"
          optionValue="value"
          filter
          filterBy="label,email"
          filterPlaceholder=""
          placeholder={EQUIPE_PROFISSIONAL_PLACEHOLDER}
          emptyFilterMessage={EQUIPE_PROFISSIONAL_EMPTY}
          emptyMessage={profissionaisError ?? EQUIPE_PROFISSIONAL_EMPTY}
          disabled={disabled}
          aria-label={`Profissional ${indice + 1}`}
          valueTemplate={(opcao: ProfissionalOpcao | null) =>
            opcao ? (
              <span className={styles.valorNome}>
                <span
                  className={styles.truncavel}
                  data-pr-tooltip={opcao.label}
                  data-pr-position="top"
                >
                  {opcao.label}
                </span>
                <SituacaoTags situacao={situacaoDe(opcao)} />
              </span>
            ) : (
              <span>{EQUIPE_PROFISSIONAL_PLACEHOLDER}</span>
            )
          }
          itemTemplate={(opcao: ProfissionalOpcao) => (
            <div className={styles.opcao}>
              <span className={styles.opcaoTexto}>
                <span className={styles.opcaoNome}>{opcao.label}</span>
                <span className={styles.opcaoEmail}>{opcao.email}</span>
              </span>
              <SituacaoTags situacao={situacaoDe(opcao)} />
            </div>
          )}
          onChange={(event) => escolherProfissional(event.value)}
        />
      </FormField.Control>
      <FormField.Error />
    </FormField.Root>
  );
}

/** Papel na obra: leitura (texto) ou o select de papéis. */
export function PapelCelulaView({
  indice,
  linha,
  papeis,
  disabled = false,
  nome,
  editavel,
  testId,
  escolherPapel,
}: PapelCelulaProps & PapelCelulaViewModel) {
  if (!editavel) {
    return (
      <span className={styles.truncavel} data-pr-tooltip={nome} data-pr-position="top">
        {nome}
      </span>
    );
  }

  return (
    <Dropdown
      size="sm"
      className={styles.select}
      pt={PT_SELECT}
      value={linha.papelObraId}
      options={papeis}
      optionLabel="label"
      optionValue="value"
      placeholder={EQUIPE_PAPEL_PLACEHOLDER}
      disabled={disabled}
      valueTemplate={(papel: PapelOpcao | null) =>
        papel ? (
          <span className={styles.valor}>{papel.label}</span>
        ) : (
          <span>{EQUIPE_PAPEL_PLACEHOLDER}</span>
        )
      }
      aria-label={`Papel na obra ${indice + 1}`}
      onChange={(event) => escolherPapel(event.value)}
      data-testid={testId}
    />
  );
}

/** Data Início / Data Fim: leitura (dd/mm/aaaa) ou o calendário, com o ícone dentro do campo (Figma). */
export function DataCelulaView({
  indice,
  linha,
  campo,
  disabled = false,
  dataFormatada,
  dataSelecionada,
  alterarData,
}: DataCelulaProps & DataCelulaViewModel) {
  const id = `equipe-obra-${campo}-${indice}`;
  const rotulo = campo === "inicio" ? EQUIPE_COLUMN_INICIO : EQUIPE_COLUMN_FIM;

  if (!linha.editing) return <span>{dataFormatada}</span>;

  return (
    <FormField.Root className={styles.campo} id={id} testId={id} error={linha.errors[campo]}>
      <span className={styles.data}>
        <Icon name="calendar" className={styles.dataIcone} />
        <FormField.Control>
          <Calendar
            value={dataSelecionada}
            aria-label={rotulo}
            placeholder={EQUIPE_DATA_PLACEHOLDER}
            disabled={disabled}
            className={styles.calendario}
            inputClassName={styles.dataInput}
            showIcon={false}
            onChange={(event) => alterarData(event.value)}
          />
        </FormField.Control>
        <Icon name="chevron-down" className={styles.dataSeta} />
      </span>
      <FormField.Error />
    </FormField.Root>
  );
}

/** Ações da linha: ✓ / ✗ na edição; lápis e lixeira depois de confirmada (botões de 28px do Figma). */
export function AcoesCelulaView({
  linha,
  disabled = false,
  rotuloEditar,
  rotuloExcluir,
  confirmarBloqueado,
  podeExcluir,
  testIds,
  onConfirm,
  onCancel,
  onEdit,
  onRemove,
}: AcoesCelulaProps & AcoesCelulaViewModel) {
  const botao = (
    icone: ReactNode,
    rotulo: string,
    acao: () => void,
    testId: string,
    bloqueado = false,
  ) => (
    <Button
      type="button"
      icon={icone}
      rounded
      text
      size="small"
      className={styles.acao}
      aria-label={rotulo}
      disabled={disabled || bloqueado}
      onClick={acao}
      data-testid={testId}
    />
  );

  return (
    <div className={styles.acoes}>
      {linha.editing ? (
        <>
          {botao(
            <CheckIcon aria-hidden />,
            "Confirmar",
            onConfirm,
            testIds.confirm,
            confirmarBloqueado,
          )}
          {botao(<TimesIcon aria-hidden />, "Cancelar edição", onCancel, testIds.cancel)}
        </>
      ) : (
        <>
          {botao(<PencilIcon aria-hidden />, rotuloEditar, onEdit, testIds.edit)}
          {podeExcluir
            ? botao(<TrashIcon aria-hidden />, rotuloExcluir, onRemove, testIds.remove)
            : null}
        </>
      )}
    </div>
  );
}
